import { EditableContent } from '../../../edit/EditableContent';
import { pickBlockField } from '../../../lib/localized';
import { parseMenuColumns } from '../../../lib/parseMenuColumns';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName, MenuItemOrderCode, MenuItemPriceSlot } from '../MenuItemTags';
import { MenuLegalLegend } from '../MenuLegalLegend';
import {
  isMenuColumnActive,
  isMenuItemActive,
  menuItemHasContent,
} from '../menuEditHelpers';

/** Sommelier ledger — one ruled region after another, glass price on the trailing edge. */
export function DrinksListCellar({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const menuDataRaw = pickBlockField(block.content, 'menuData', locale);
  const columns = parseMenuColumns(menuDataRaw);
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-drinks-cellar', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="drinks"
    >
      <div className="ws-drinks-cellar__inner">
        <header className="ws-drinks-cellar__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-drinks-cellar__title"
          >
            {sectionTitle}
          </EditableContent>
          {sectionSubtitle || editable ? (
            <EditableContent
              fieldKey="sectionSubtitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-drinks-cellar__subtitle"
            >
              {sectionSubtitle}
            </EditableContent>
          ) : null}
        </header>
        <div className="ws-drinks-cellar__book">
          {columns.map((column, columnIndex) => {
            const columnActive = isMenuColumnActive(target, columnIndex);
            return (
              <div key={`${column.title}-${columnIndex}`} className="ws-drinks-cellar__region">
                {column.title.trim() ? (
                  <h3
                    className={[
                      'ws-drinks-cellar__region-title',
                      columnActive ? 'ws-edit-child--active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    {...(editable
                      ? {
                          'data-edit-child': 'menu-column' as const,
                          'data-column-index': String(columnIndex),
                        }
                      : {})}
                  >
                    {column.title}
                  </h3>
                ) : null}
                <ul className="ws-drinks-cellar__bins">
                  {column.items.map((item, itemIndex) => {
                    if (!menuItemHasContent(item)) {
                      return null;
                    }
                    const itemActive = isMenuItemActive(
                      target?.kind === 'menu-item' ? target : null,
                      columnIndex,
                      itemIndex,
                    );
                    return (
                      <li
                        key={`${item.name}-${itemIndex}`}
                        className={[
                          'ws-drinks-cellar__bin',
                          itemActive ? 'ws-edit-child--active' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        {...(editable
                          ? {
                              'data-edit-child': 'menu-item' as const,
                              'data-column-index': String(columnIndex),
                              'data-item-index': String(itemIndex),
                            }
                          : {})}
                      >
                        <div className="ws-menu__item-row">
                          <MenuItemOrderCode code={item.code} />
                          <div className="ws-menu__item-copy">
                            <MenuItemName item={item} locale={locale} showOrderCode={false} />
                            {item.description ? (
                              <p className="ws-drinks-cellar__origin">{item.description}</p>
                            ) : null}
                            <MenuItemDietaryTags item={item} locale={locale} />
                          </div>
                          <MenuItemPriceSlot
                            price={item.price}
                            className="ws-drinks-cellar__glass"
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
        <MenuLegalLegend columns={columns} locale={locale} />
      </div>
    </section>
  );
}
